import React, { useState } from "react";
import {
  Drawer,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Avatar,
  alpha,
  Badge,
  Paper,
  Zoom,
  Collapse,
  useTheme
} from "@mui/material";
import {
  getValue,
  IsSmallScreen,
  LoacalVariables
} from "../../commonFunctions/CommonFunctions";
import { getMenu } from "./menu";
// import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link, useLocation } from "react-router-dom";
import logo from '../../images/melp-logo-2.svg'
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useAuth } from "../../Context/AuthContext";

interface MenuItem {
  id: number;
  label: string;
  path?: string;
  icon: React.ElementType;
  children?: MenuItem[];
}

interface SidebarProps {
  open: boolean;
  handleDrawerToggle: () => void;
  handleCollapse: () => void;
  collapse: boolean;
  drawerWidth?: number;

  primaryColor: string;
  secondaryColor: string;
  hoverColor: string;
  textColor: string;
  iconColor: string;
  hoverTextColor: string;
  backgroundColor: string;
  selectedBackgroundColor: string;
  boxShadowColor: string;
}

const Sidebar2: React.FC<SidebarProps> = ({
  open,
  handleDrawerToggle,
  collapse,
  drawerWidth = 280,
  textColor,
}) => {
  const theme = useTheme();
  const isMobile = IsSmallScreen();
  const location = useLocation(); // Get the current location from React Router

  const { handleLogout } = useAuth();

  const [hoveredMenuPosition, setHoveredMenuPosition] = useState({ top: 0 });

  const userType: any = getValue(LoacalVariables.UserType);
  const [openChildMenus, setOpenChildMenus] = useState<Record<number, boolean>>(
    {}
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [hoveredMenuId, setHoveredMenuId] = useState<number | null>(null);

  // Helper function to determine if the menu item is selected
  const isSelected = (id: number, path?: string) =>
    location.pathname === path && selectedId === id;


  const menu = getMenu(userType);
  const userName = getValue(LoacalVariables.Name)

  const handleToggleChildMenu = (id: number) => {
    setOpenChildMenus((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleMouseEnter = (id: number) => {
    setHoveredMenuId(id);

    // Find the hovered list item position
    const listItem = document.getElementById(`menu-item-${id}`);
    if (listItem) {
      const rect = listItem.getBoundingClientRect();
      setHoveredMenuPosition({ top: rect.top + window.scrollY });
    }
  };

  const handleMouseLeave = () => {
    setHoveredMenuId(null);
  };

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      onClose={handleDrawerToggle}
      sx={{
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: collapse ? 88 : drawerWidth,
          overflowX: "hidden",
          transition: theme.transitions.create(['width', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeInOut,
          }),
          border: "none",
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          position: "fixed",
          top: 64,
          height: "calc(100vh - 64px)",
          left: 0,
          borderRadius: 0,
          backgroundColor: theme.palette.background.default,
          boxShadow: `4px 0 20px ${alpha(theme.palette.common.black, 0.05)}`,
          backgroundImage: `linear-gradient(180deg, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 0%, transparent 100%)`,
        },
      }}
    >
      {/* Logo Section with Toggle Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapse ? "center" : "space-between",
          padding: collapse ? "1rem 0" : "1.2rem 1.5rem",
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          minHeight: 80,
        }}
      >
        {!collapse ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.2rem",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                letterSpacing: "-0.5px",
              }}
            >
              {userName || "Business Name"}
            </Typography>
          </Box>
        ) : (
          <Tooltip title="Toggle Sidebar" placement="right">
            <Avatar
              src={logo}
              alt="logo"
              variant="rounded"
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Dashboard Section Header */}
      {!collapse && (
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: alpha(textColor, 0.5),
              fontSize: "0.7rem",
            }}
          >
            Main Menu
          </Typography>
        </Box>
      )}

      <List sx={{ px: collapse ? 1 : 2, py: 1 }}>
        {menu.staticMenu.map((item: any) => (
          <React.Fragment key={item.id}>
            {/* Main Menu Item */}
            <Tooltip
              title={collapse ? item.label : ""}
              placement="right"
              arrow
              disableHoverListener={!collapse}
            >
              <ListItemButton
                component={Link}
                to={item.path || "#"}
                onClick={() => {
                  setSelectedId(item.id);
                  if (item.children) handleToggleChildMenu(item.id);
                }}
                onMouseEnter={() => handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
                sx={{
                  minHeight: 48,
                  justifyContent: collapse ? "center" : "flex-start",
                  px: collapse ? 1 : 2,
                  py: 1.5,
                  mb: 0.5,
                  borderRadius: 3,
                  position: "relative",
                  overflow: "hidden",
                  backgroundColor: isSelected(item.id, item.path)
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "transparent",
                  color: isSelected(item.id, item.path) ? theme.palette.primary.main : textColor,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "0 4px 4px 0",
                    transform: isSelected(item.id, item.path) ? "scaleY(1)" : "scaleY(0)",
                    transition: "transform 0.2s ease",
                  },
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.main,
                      transform: "scale(1.1)",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapse ? "auto" : 40,
                    mr: collapse ? 0 : 2,
                    color: isSelected(item.id, item.path) ? theme.palette.primary.main : alpha(textColor, 0.7),
                    transition: "all 0.2s ease",
                    justifyContent: "center",
                  }}
                >
                  <Badge
                    color="error"
                    variant="dot"
                    invisible={!item.badge}
                    sx={{
                      "& .MuiBadge-badge": {
                        right: -2,
                        top: 2,
                      },
                    }}
                  >
                    <item.icon sx={{ fontSize: collapse ? 24 : 22 }} />
                  </Badge>
                </ListItemIcon>

                {!collapse && (
                  <>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "0.95rem",
                          fontWeight: isSelected(item.id, item.path) ? 600 : 500,
                        },
                      }}
                    />
                    {item.children && (
                      <ListItemIcon sx={{ minWidth: "auto", ml: 1 }}>
                        {openChildMenus[item.id] ? (
                          <ExpandLess sx={{ fontSize: 20, color: alpha(textColor, 0.7) }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 20, color: alpha(textColor, 0.7) }} />
                        )}
                      </ListItemIcon>
                    )}
                  </>
                )}
              </ListItemButton>
            </Tooltip>

            {/* Child Menu Items (Collapsed Mode - Floating) */}
            {item.children && collapse && hoveredMenuId === item.id && (
              <Zoom in={true} style={{ transitionDelay: "0.1s" }}>
                <Paper
                  elevation={8}
                  sx={{
                    position: "fixed",
                    left: 88,
                    top: hoveredMenuPosition.top,
                    zIndex: 2000,
                    minWidth: 200,
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: `0 10px 40px ${alpha(theme.palette.common.black, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                  onMouseEnter={() => setHoveredMenuId(item.id)}
                  onMouseLeave={() => setHoveredMenuId(null)}
                >
                  <Box sx={{ py: 1 }}>
                    <Typography
                      sx={{
                        px: 2,
                        py: 1,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: alpha(textColor, 0.5),
                        letterSpacing: "0.5px",
                      }}
                    >
                      {item.label}
                    </Typography>
                    {item.children.map((child: any) => (
                      <ListItemButton
                        key={child.id}
                        component={Link}
                        to={child.path || "#"}
                        onClick={() => setSelectedId(child.id)}
                        sx={{
                          px: 2,
                          py: 1.5,
                          transition: "all 0.2s ease",
                          backgroundColor: isSelected(child.id, child.path)
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                          color: isSelected(child.id, child.path) ? theme.palette.primary.main : textColor,
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            pl: 3,
                          },
                        }}
                      >
                        <ListItemText
                          primary={child.label}
                          sx={{
                            "& .MuiTypography-root": {
                              fontSize: "0.9rem",
                              fontWeight: isSelected(child.id, child.path) ? 600 : 400,
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </Box>
                </Paper>
              </Zoom>
            )}

            {/* Child Menu Items (Expanded Mode) */}
            {item.children && !collapse && (
              <Collapse in={openChildMenus[item.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child: any) => (
                    <Tooltip
                      key={child.id}
                      title={collapse ? child.label : ""}
                      placement="right"
                      arrow
                    >
                      <ListItemButton
                        component={Link}
                        to={child.path || "#"}
                        onClick={() => setSelectedId(child.id)}
                        sx={{
                          pl: 7,
                          py: 1,
                          mb: 0.5,
                          borderRadius: 2,
                          backgroundColor: isSelected(child.id, child.path)
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                          color: isSelected(child.id, child.path) ? theme.palette.primary.main : textColor,
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                          },
                        }}
                      >
                        <ListItemText
                          primary={child.label}
                          sx={{
                            "& .MuiTypography-root": {
                              fontSize: "0.85rem",
                              fontWeight: isSelected(child.id, child.path) ? 600 : 400,
                            },
                          }}
                        />
                      </ListItemButton>
                    </Tooltip>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Bottom Section */}
      <Box sx={{ flexGrow: 1 }} />

      <Divider sx={{ mx: 2, my: 2, opacity: 0.5 }} />

      <List sx={{ px: collapse ? 1 : 2, pb: 2 }}>
        {/* Settings */}
        {/* <Tooltip title={collapse ? "Settings" : ""} placement="right" arrow>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: collapse ? "center" : "flex-start",
              px: collapse ? 1 : 2,
              py: 1.5,
              borderRadius: 3,
              color: alpha(textColor, 0.7),
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: collapse ? "auto" : 40,
                mr: collapse ? 0 : 2,
                color: "inherit",
                justifyContent: "center",
              }}
            >
              <SettingsIcon sx={{ fontSize: collapse ? 24 : 22 }} />
            </ListItemIcon>
            {!collapse && <ListItemText primary="Settings" />}
          </ListItemButton>
        </Tooltip> */}

        {/* Logout */}
        <Tooltip title={collapse ? "Logout" : ""} placement="right" arrow>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: collapse ? "center" : "flex-start",
              px: collapse ? 1 : 2,
              py: 1.5,
              borderRadius: 3,
              color: alpha(theme.palette.error.main, 0.8),
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                color: theme.palette.error.main,
              },
            }}
            onClick={handleLogout}
          >
            <ListItemIcon
              sx={{
                minWidth: collapse ? "auto" : 40,
                mr: collapse ? 0 : 2,
                color: "inherit",
                justifyContent: "center",
              }}
            >
              <LogoutIcon sx={{ fontSize: collapse ? 24 : 22 }} />
            </ListItemIcon>
            {!collapse && <ListItemText primary="Logout" />}
          </ListItemButton>
        </Tooltip>
      </List>
    </Drawer>
  );
};

export default Sidebar2;
